import { Badge } from "@/components/ui/badge";
import { Table2 } from "lucide-react";

import type { Spreadsheet } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

// Type based on models.ts from nango.yaml
export type SpreadsheetSheet = {
  id: any;
  platform: string;
  title: string;
  index: number;
};

export { Spreadsheet };

type SpreadsheetCardProps = Spreadsheet & BaseEntityCardProps;

// Avatar component for SpreadsheetCard
export function SpreadsheetCardAvatar({
  className,
  style,
}: Partial<SpreadsheetCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <Table2 className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function SpreadsheetCard({
  title,
  platform = "google-sheet",
  web_link,
  sheets,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: SpreadsheetCardProps) {
  const sheetsCount = sheets?.length || 0;

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <SpreadsheetCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{title}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {web_link && (
              <BaseEntityCardExternalLinkAction
                href={web_link}
                tooltip="Open spreadsheet"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <BaseEntityCardDescription>
            <span>Google Sheet</span>
            {sheetsCount > 0 && (
              <span className="ml-4">
                {sheetsCount} sheet{sheetsCount === 1 ? "" : "s"}
              </span>
            )}
          </BaseEntityCardDescription>

          {sheets && sheets.length > 0 && (
            <BaseEntityCardTags>
              {sheets.slice(0, 3).map((sheet) => (
                <Badge key={sheet.id} variant="outline" className="text-xs">
                  {sheet.title}
                </Badge>
              ))}
              {sheets.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{sheets.length - 3} more
                </Badge>
              )}
            </BaseEntityCardTags>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
